import { Rocket } from "lucide-react";
import type { HelpArticle } from "../types";

export const gettingStartedArticles: HelpArticle[] = [
  {
    id: "getting-started",
    title: "Getting started",
    icon: Rocket,
    group: "getting-started",
    summary: "What RIoT-FOTA does, how roles work, and the path to your first firmware rollout.",
    keywords: ["overview", "intro", "onboarding", "first steps", "welcome", "home", "roles", "permissions"],
    overview:
      "RIoT-FOTA is how you manage a fleet of IoT devices and push firmware and file updates to them over the air — safely, in controlled batches, with a full record of what changed.\n\n" +
      "The typical flow is: your devices appear here automatically as they come online, you **group** them, you **upload** a firmware build or a file bundle, then you create a **task** that rolls that update out to a group. You watch progress per device, and every change is written to the **Audit Log**.\n\n" +
      "Use the **?** button in the top bar on any page to open the guide for the section you are looking at. Use the search box at the top of the Help Center to jump straight to an answer.",
    concepts: [
      {
        term: "Company (tenant)",
        definition:
          "Your isolated workspace. Your devices, users, firmware and files are only ever visible inside your own company. Riot IO operates the platform and can support you, but companies never see each other's data.",
      },
      {
        term: "Role",
        definition:
          "What you are allowed to do. There are four: Viewer (read-only), Operator (can run rollouts), Company admin (manages users, keys, firmware and settings) and Platform admin (Riot IO staff who manage every company).",
      },
      {
        term: "Device",
        definition:
          "A single physical unit, identified by its IMEI. It reports a status (online/offline), a firmware version, and a configuration (its 'shadow').",
      },
      {
        term: "Device group",
        definition: "A named set of devices you target together — e.g. 'EU field units' or 'Beta testers'. Rollouts target groups.",
      },
      {
        term: "Firmware / File (Resource)",
        definition:
          "Firmware is a versioned device build. A Resource is a bundle of supporting files (web assets, certificates, config) delivered to the device's on-board storage. Both are rolled out with tasks.",
      },
      {
        term: "Task",
        definition:
          "A rollout job. It takes an update (firmware or resource) and applies it to a target (a group or set of devices), then tracks success/failure per device.",
      },
    ],
    tasks: [
      {
        id: "first-rollout",
        title: "Run your first firmware rollout end to end",
        intro: "The shortest path from a fresh account to firmware landing on real devices.",
        steps: [
          { text: "Open **Devices** and confirm your units are appearing and reporting a status. Devices self-register as they connect for the first time." },
          { text: "Open **Device groups** and create a group, then add the devices you want to update (or use a filter so devices join automatically)." },
          { text: "Open **Firmware**, choose **Upload firmware**, pick the device model from the catalogue, set the version, attach the build, and mark it **Active** when ready.", note: "Only a Company admin can upload firmware." },
          { text: "Open **Tasks**, choose **Create task**, select the firmware update, target your group, and start the rollout.", note: "You need at least the Operator role to create a task." },
          { text: "Open the task to watch per-device progress. Devices apply the update when they next check in." },
        ],
        result: "Your group receives the firmware over the air, and the whole rollout is recorded in the Audit Log.",
      },
      {
        id: "find-your-way",
        title: "Find the right section for what you need",
        steps: [
          { text: "**Devices / Device groups / Device logs** — see and organise your fleet, and read what a device has been doing." },
          { text: "**Tasks** — start and monitor firmware and file rollouts." },
          { text: "**Firmware / Resources** — manage the builds and file bundles you roll out." },
          { text: "**Company / Users / Group Keys / API Credentials / Usage / Audit Log** — administration, under the **Management** heading (Company admin only)." },
          { text: "**Profile / Passkeys / Notifications** — your own account and sign-in security." },
        ],
      },
    ],
    faqs: [
      {
        q: "Why can't I see some menu items?",
        a: "The menu is role-aware. Management items (Users, Company, Group Keys, API Credentials, Usage, Audit Log) only appear for Company admins, and the Platform Admin section only appears for Riot IO staff. If you need access, ask a Company admin to raise your role.",
      },
      {
        q: "Do I have to configure anything before devices show up?",
        a: "No. Devices register themselves the first time they connect using a group provisioning key. If nothing is appearing, check Group Keys and confirm the fleet was flashed with a valid key.",
      },
      {
        q: "Where do I see what changed and who changed it?",
        a: "The Audit Log records every meaningful action — rollouts, firmware changes, user and settings changes — with who did it and when. It is read-only and cannot be edited.",
      },
    ],
    roleMatrix: [
      { capability: "View devices, groups, firmware, tasks", viewer: "yes", operator: "yes", tenant_admin: "yes", super_admin: "yes" },
      { capability: "Create and run rollout tasks", viewer: "no", operator: "yes", tenant_admin: "yes", super_admin: "yes" },
      { capability: "Upload firmware / resources, edit device settings", viewer: "no", operator: "no", tenant_admin: "yes", super_admin: "yes" },
      { capability: "Manage users, keys, billing, audit log", viewer: "no", operator: "no", tenant_admin: "yes", super_admin: "yes" },
      { capability: "Manage all companies & device types", viewer: "no", operator: "no", tenant_admin: "no", super_admin: "yes" },
    ],
    related: ["devices", "device-groups", "tasks", "firmware"],
  },
];
